import { useEffect, useState, useRef } from "react";
import { api, media } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Camera, Trophy, Trash2 } from "lucide-react";

const MFIELDS = [["weight_kg", "Weight (kg)"], ["waist_cm", "Waist"], ["chest_cm", "Chest"], ["hip_cm", "Hip"], ["thigh_cm", "Thigh"], ["arm_cm", "Arm"], ["neck_cm", "Neck"], ["body_fat", "Body fat %"]];

export const Progress = ({ accessId }) => {
  const [m, setM] = useState({ entries: [], trend: [], baseline: null });
  const [milestones, setMilestones] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({});
  const [cmp, setCmp] = useState([null, null]);
  const fileRef = useRef();

  const load = () => {
    api.get(`/programs/${accessId}/measurements`).then(({ data }) => setM(data)).catch(() => {});
    api.get(`/programs/${accessId}/milestones`).then(({ data }) => setMilestones(data)).catch(() => {});
    api.get(`/programs/${accessId}/photos`).then(({ data }) => setPhotos(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accessId]);

  const save = async () => {
    if (!Object.keys(form).length) return;
    await api.post(`/programs/${accessId}/measurements`, form);
    toast.success("Progress logged"); setForm({}); load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/programs/${accessId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo added (private)"); load(); }
    catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const delPhoto = async (id) => { await api.delete(`/programs/photo/${id}`); load(); };

  const first = m.trend[0], last = m.trend[m.trend.length - 1];
  const net = first && last ? (last.weight_kg - first.weight_kg).toFixed(1) : null;

  return (
    <div className="pt-6" data-testid="program-progress">
      <div className="mono-label">Measured, not guessed</div>
      <h1 className="font-serif text-4xl text-white mb-4">My Progress</h1>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {[["Baseline", m.baseline ? `${m.baseline} kg` : "—"], ["Latest", last ? `${last.weight_kg} kg` : "—"], ["Net change", net !== null ? `${net} kg` : "—"]].map(([l, v]) => (
          <div key={l} className="glass rounded-2xl p-4"><div className="font-mono text-2xl text-cyan-300">{v}</div><div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</div></div>
        ))}
      </div>

      {m.trend.length > 1 && (
        <div className="glass rounded-2xl p-4 mb-5" data-testid="weight-chart">
          <div className="mono-label mb-3">Weight trend</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={m.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ background: "#0a0e17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Line type="monotone" dataKey="weight_kg" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="glass rounded-2xl p-5 mb-5">
        <div className="mono-label mb-3">Log measurements</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {MFIELDS.map(([k, l]) => (
            <div key={k}><label className="text-[11px] text-slate-400">{l}</label>
              <Input type="number" value={form[k] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value === "" ? undefined : Number(e.target.value) }))} className="bg-black/40 border-white/10" data-testid={`measure-${k}`} /></div>
          ))}
        </div>
        <Button onClick={save} className="btn-primary rounded-lg mt-4" data-testid="measure-save">Save entry</Button>
      </div>

      {milestones.length > 0 && (
        <div className="glass rounded-2xl p-5 mb-5" data-testid="milestones">
          <div className="mono-label mb-3">Milestones</div>
          <div className="flex flex-wrap gap-2">
            {milestones.map((ms) => (
              <span key={ms.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-xs"><Trophy size={12} /> {ms.label}</span>
            ))}
          </div>
        </div>
      )}

      <div className="glass rounded-2xl p-5" data-testid="progress-photos">
        <div className="flex items-center justify-between mb-3">
          <div className="mono-label">Private progress gallery</div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="photo-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="photo-upload"><Camera size={13} className="mr-1.5" /> Add photo</Button>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Photos are private to you. Stored securely, never public, not indexed by search engines.</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-xl overflow-hidden border border-white/10 bg-black/40" data-testid={`photo-${p.id}`}>
              <img src={media(p.url)} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => setCmp((c) => (!c[0] ? [p, c[1]] : [c[0], p]))} />
              <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 p-1 rounded bg-black/70 opacity-0 group-hover:opacity-100"><Trash2 size={11} className="text-rose-400" /></button>
              <div className="absolute bottom-0 inset-x-0 bg-black/70 text-[9px] font-mono text-slate-300 text-center py-0.5">{p.created_at?.slice(0, 10)}</div>
            </div>
          ))}
          {photos.length === 0 && <div className="col-span-full text-center text-slate-500 text-xs py-6">No photos yet.</div>}
        </div>
        {cmp[0] && cmp[1] && (
          <div className="mt-4" data-testid="photo-compare">
            <div className="mono-label mb-2">Compare</div>
            <div className="grid grid-cols-2 gap-2">
              {cmp.map((p, i) => <div key={i} className="rounded-xl overflow-hidden border border-white/10"><img src={media(p.url)} alt="" className="w-full" /><div className="text-[10px] font-mono text-slate-400 text-center py-1">{p.created_at?.slice(0, 10)}</div></div>)}
            </div>
            <button onClick={() => setCmp([null, null])} className="text-[11px] text-slate-500 mt-2">Clear comparison</button>
          </div>
        )}
      </div>
    </div>
  );
};
